import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { sortBy } from 'lodash';
import { takeUntil, debounceTime, distinctUntilChanged, Subject, map } from 'rxjs';
import { GovernmentOffice, GovernmentOfficeType, GovernmentOffices } from 'src/app/models/government.model';
import { GovernmentService } from 'src/app/services/government.service';

@Component({
  selector: 'tx-dialog-officials-directory',
  templateUrl: './dialog-officials-directory.component.html',
  styleUrls: ['./dialog-officials-directory.component.scss'],
})
export class DialogOfficialsDirectoryComponent implements OnInit, OnDestroy {
  officesPerType: GovernmentOffices = [];
  offices: GovernmentOffice[] = [];
  filteredOffices: GovernmentOffice[] = [];
  selectedOfficeType: GovernmentOfficeType | null = null;
  selectedOffice!: GovernmentOffice;

  searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  @ViewChild('filterInput') filterInput!: ElementRef;
  filterText = '';
  loading = true;

  constructor(public modal: NgbActiveModal, public governmentService: GovernmentService) {}

  ngOnInit(): void {
    this.subscribeToOffices();
    this.initFiltering();
  }

  subscribeToOffices() {
    this.governmentService
      .getOffices()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.initOfficeData(response));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initOfficeData(response: GovernmentOffices) {
    this.officesPerType = response;
    // select first office from list
    this.selectedOfficeType = response[0];
    this.setOffices();
    this.loading = false;
  }

  onSelectOfficeType(event: any): void {
    const selectedValue = parseInt(event.target.value);
    const selectedOfficeType = this.officesPerType.find((type) => type.officeType === selectedValue);
    if (selectedOfficeType) {
      this.selectedOfficeType = selectedOfficeType;
      this.setOffices();
      this.filteredOffices = this.filterItems(this.filterText);
    }
  }

  onSelectOffice(event: any): void {
    const selectedValue = event.target.value;
    this.selectedOffice = this.filteredOffices.find(
      (office) => office.officeName === selectedValue
    ) as GovernmentOffice;
  }

  setOffices() {
    const officesForType = this.officesPerType.find((type) => type.officeType === this.selectedOfficeType?.officeType);
    this.offices = officesForType ? sortBy(officesForType.offices, ['officeName']) : [];
    this.filteredOffices = this.offices;
  }

  initFiltering() {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroy$), // Unsubscribe when the component is destroyed
        debounceTime(250), // wait 250ms after each keystroke before considering the term
        map((term) => term.trim()), // Remove whitespace from begin or end
        distinctUntilChanged() // ignore new term if same as previous term
      )
      .subscribe({
        next: (searchTerm) => {
          this.filteredOffices = searchTerm.length > 1 ? this.filterItems(searchTerm) : this.offices;
        },
      });
  }

  filterItems(searchTerm: string): GovernmentOffice[] {
    return this.offices.filter((item) => item.officeName.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1);
  }

  clearFilter() {
    this.filteredOffices = this.offices;
    this.filterText = '';
  }

  onFilterChange() {
    this.searchTerm$.next(this.filterText);
  }
}
